import { useEffect } from 'react'
import { type Section } from '@/features/cms/components/CMS/types'
import { CouponsContextProvider } from '@/features/shop/components/Coupons/CouponsContext'
import {
  getCouponsFromCMSContent,
  loadBeaconsFromCMSContent,
  trackCmsExperimentsInDatadog,
} from '@/features/cms/components/CMS/utils'
import { getComponentForContentTypeId } from '@/features/cms/components/CMS/componentUtils'
import { CMSContextProvider } from '@/features/cms/components/CMS/CMSContext'
import { DeferredShelf } from '@/features/cms/components/CMS/DeferredShelf'
import { useAtomValue } from 'jotai'
import { dismissedCMSContentAtom } from '@/features/shared/state/DismissedCMSContent/atoms'
import { type cmsComponents } from '@/features/cms/components/CMS/CMSComponents'

export const CMSSection = ({
  section,
  layoutEagerComponents,
}: {
  section: Section
  layoutEagerComponents?: Partial<typeof cmsComponents>
}) => {
  const dismissedAnalyticSlugs = useAtomValue(dismissedCMSContentAtom)
  useEffect(() => {
    loadBeaconsFromCMSContent(section)
    trackCmsExperimentsInDatadog(section)
  }, [section])

  return (
    <>
      {section.map((data, idx) => {
        const { content_type_id, id, analytic_slug } = data

        const isDismissed = dismissedAnalyticSlugs.includes(analytic_slug ?? '')
        if (isDismissed) return null
        const Component = getComponentForContentTypeId({
          contentTypeId: content_type_id,
          layoutEagerComponents,
        })
        if (!Component) return null

        return (
          <DeferredShelf
            data={data}
            // It's possible for the same content to be used multiple times on
            // the same page, so we can't rely on the id alone.
            // oxlint-disable-next-line react/no-array-index-key
            key={`${id}-${idx}`}
            index={idx}
          >
            {(resolvedData) => (
              <CMSContextProvider data={resolvedData}>
                <CouponsContextProvider coupons={getCouponsFromCMSContent(resolvedData)}>
                  <Component {...resolvedData} index={idx} />
                </CouponsContextProvider>
              </CMSContextProvider>
            )}
          </DeferredShelf>
        )
      })}
    </>
  )
}
